import {
  ClipboardPen, FlaskConical, Siren, Handshake, HeartPlus, Droplets, ClipboardList, ListOrdered, HeartPulse, Presentation, CalendarDays,
} from 'lucide-react';
import { thiCvor } from './thiCvor';

/*
 * THI CV ICU rotation: the VAD/transplant and Red cardiac surgery ICU teams at
 * St. Luke's (listed on Blackboard as the "SLICU" rotation). Extends the CVOR rotation
 * (see components/hospital/registry.js) for its tools; the CVOR contacts, locations and
 * documents are included after the ICU-specific ones. Built from the SLICU Rotation
 * Orientation (Dr Yang). See docs/blackboard-bslmc.md.
 */

const BB_DOC = (id) => `https://bcm.blackboard.com/ultra/courses/_129_1/document/${id}?view=content&state=view`;
const SLICU = BB_DOC('_261925_1');

export const thiCvIcu = {
  accent: '#9F8D70',
  notesKey: 'notes:texas-heart:cv-icu',
  tagline: 'VAD and Red teams: pre-rounds, presenting, order sets',

  toolSections: [
    { title: 'Clinical tools', tools: [
      { id: 'prerounds', title: 'Pre-Rounds & Presenting', subtitle: 'What to check, what to say', icon: ClipboardList, action: 'guide:prerounds', featured: true },
      { id: 'handoff', title: 'Postop Handoff', subtitle: 'What to expect from the OR team', icon: Handshake, action: 'handoff' },
      { id: 'ordersets', title: 'Epic Order Sets', subtitle: 'The ones to favourite', icon: ListOrdered, action: 'guide:ordersets' },
      { id: 'devices', title: 'Devices & RV Failure', subtitle: 'What you will meet, how the right heart fails', icon: HeartPulse, action: 'guide:devices' },
      { id: 'eras', title: 'Enhanced Recovery', subtitle: 'ERAS protocols by procedure', icon: HeartPlus, action: 'eras' },
      { id: 'insulin', title: 'Insulin Infusion', subtitle: 'Starting and adjusting the drip', icon: Droplets, action: 'insulin' },
    ] },
    { title: 'Logistics', tools: [
      { id: 'teams', title: 'The Two Teams', subtitle: 'VAD and Red: rooms, hours, call', icon: Presentation, action: 'guide:teams' },
      { id: 'day', title: 'Getting Started', subtitle: 'First day, Epic context, weekends, time off', icon: CalendarDays, action: 'guide:day' },
      { id: 'epic', title: 'Epic', subtitle: 'What to document and where', icon: ClipboardPen, action: 'epic' },
      { id: 'labs', title: 'Sending Labs', subtitle: 'Tubes, ACTs and where they go', icon: FlaskConical, action: 'labs' },
      { id: 'airways', title: 'Airways & Codes', subtitle: 'Pagers, coverage, units', icon: Siren, action: 'airways-codes' },
    ] },
  ],

  contacts: {
    storageKey: 'thi-cv-icu-favorites',
    groups: [
      { id: 'teams', title: 'ICU teams', open: true, items: [
        { label: 'VAD team phone', phone: '8328466324' },
        { label: 'Blue team', phone: '2817265662' },
        { label: 'Red team', phone: '8328466318' },
      ] },
      ...thiCvor.contacts.groups,
    ],
  },

  locations: {
    ...thiCvor.locations,
    groups: [
      { id: 'icu-units', title: 'Units', items: [
        { name: 'CV Recovery', floor: '2nd floor', note: 'Red team' },
        { name: '8 Cooley A and B', floor: '8th floor', note: 'VAD team' },
      ] },
      { id: 'icu-rooms', title: 'Team rooms', items: [
        { name: 'VAD team room', floor: '8th floor', where: 'Green elevators. Hallway between the 8CA nurses’ stations', code: '8505*', codeLabel: 'Door' },
        { name: 'Red team office', floor: '2nd floor', where: 'Purple elevators. By bays 20–26', note: 'No code' },
        { name: 'VAD call room', floor: '8th floor', where: 'Yellow elevators. Room 818', code: '9258*', codeLabel: 'Door' },
        { name: 'Red call room', floor: '7th floor', where: 'P711, between 7 South 3 and 7 South 4' },
      ] },
      { id: 'icu-supplies', title: 'ICU supplies', items: [
        { name: 'Line cart', floor: '8th floor', code: '1523', codeLabel: 'Cart', codeNote: 'Then Enter' },
        { name: 'Med room', floor: '8th floor', code: '6510*', codeLabel: 'Door' },
        { name: '8A supply and break room', floor: '8A', code: '9810*', codeLabel: 'Door' },
        { name: 'Nutrition', floor: '8th floor', code: '1234*', codeLabel: 'Door' },
        { name: '8B staff restroom', floor: '8B', code: '5678*', codeLabel: 'Door' },
      ] },
      ...thiCvor.locations.groups,
    ],
  },

  documents: {
    storageKey: 'thi-cv-icu-pinned-docs',
    groups: [
      { id: 'icu', title: 'CV ICU', items: [
        { title: 'ICU rotation orientation', subtitle: 'Blackboard, listed as SLICU', url: SLICU },
        { title: 'ICU articles', subtitle: 'Blackboard', url: BB_DOC('_261927_1') },
      ] },
      ...thiCvor.documents.groups,
    ],
  },

  guides: {
    prerounds: {
      title: 'Pre-Rounds & Presenting',
      segments: [
        { id: 'check', label: 'Check', sections: [
          { title: 'From sign-out', blocks: [{ type: 'bullets', items: ['Write down every overnight event'] }] },
          { title: 'In Epic, tab by tab', blocks: [{ type: 'rows', rows: [
            { label: 'Vitals', value: 'Temperature, heart rate and whether paced, BP and MAP, PA pressures, CVP, CO and CI, SpO2, ventilator settings, device settings', note: 'Ranges and trends, better or worse' },
            { label: 'Drips', value: 'What is running and what changed since yesterday' },
            { label: 'Ins and outs', value: 'Net 24 h, urine 24 h and recent hours, drains and chest tubes, dialysis, stool, NG, emesis' },
            { label: 'Labs', value: 'ABG, BMP, CBC, SvO2, LDH, lactate' },
            { label: 'Imaging', value: 'The daily chest film and anything else from 24 h' },
            { label: 'Micro', value: 'Fever, white count trend, new cultures, speciation, sensitivities' },
            { label: 'Lines, drains, airways', value: 'Date each one went in' },
            { label: 'Consults', value: 'New notes and their recommendations' },
          ] }, { type: 'note', text: 'Summary tab pages worth adding: Comp FS, Labs, Micro, Fever, Glucose, LDA, Current Meds, Med History.' }] },
        ] },
        { id: 'present', label: 'Present', sections: [
          { title: 'Order', blocks: [{ type: 'steps', items: [
            'One-liner: age, admission date, operation, postoperative day, the course so far',
            'Subjective: overnight events that matter',
            'Objective: only the relevant or abnormal vitals, device settings, ins and outs, labs, imaging. Know everything; say what matters',
            'Assessment and plan by system: the problems, the data, the plan',
          ] }] },
          { title: 'Multidisciplinary rounds', blocks: [{ type: 'bullets', items: [
            'Cardiology and cardiothoracic surgery round with you on cardiac patients. When you reach a system they cover, let them present it',
            'Bring two workstations on wheels: one for the morning chest film, one for orders as they are agreed',
          ] }] },
        ] },
      ],
      sources: [{ title: 'ICU rotation orientation', href: SLICU, detail: 'Blackboard, sign-in required' }],
    },

    ordersets: {
      title: 'Epic Order Sets',
      intro: 'Add these to favourites on day one.',
      sections: [
        { title: 'Drips and sedation', items: [
          'ICU Drips',
          'IV Insulin Titration Drip',
          'CV Insulin Transition, IV to subcutaneous',
          'Dexmedetomidine and Propofol Fast Track Extubation. The only way to order dexmedetomidine',
          'Ventilator Management and Sedation. Vent, mouth care, awakening and breathing trials',
          'Patient-Controlled Analgesia Therapy. Fentanyl is most common',
        ] },
        { title: 'Anticoagulation and blood', items: [
          'Heparin Continuous Infusion Orders, Cardiac and Cerebral Ischemia. Usual PTT goal 60–80; adjust the titration parameters',
          'Heparin Infusion for ECMO',
          'Anticoagulation for Heparin-Induced Thrombocytopenia. Also the best way to order bivalirudin',
          'Adult Blood Products',
        ] },
        { title: 'Devices and transplant', items: [
          'Left Ventricular Assist Device Pre-Op, Post-Op, and LVAD Readmission',
          'CVRR Tandem Heart and Impella Cardiac Assist Post-op',
          'CVRR Extracorporeal Life Support Post-op',
          'Cardiac Transplant Service Admission and Pre-Transplant Evaluation',
          'Transplant Cardiac Post-Op',
        ] },
        { title: 'Everything else', items: [
          'ICU and Progressive Care Electrolyte Replacement. The nurses correct electrolytes on PRN orders',
          'Adult Diabetes Management. Basal, prandial, sliding scale and glucose checks',
          'ESRD Hyperkalemia Management',
          'Adult Enteral Tube Feeding',
          'Comfort Care for End of Life',
        ] },
      ],
      sources: [{ title: 'ICU rotation orientation', href: SLICU, detail: 'Blackboard, sign-in required' }],
    },

    devices: {
      title: 'Devices & RV Failure',
      sections: [
        { title: 'Look these up before you start', blocks: [{ type: 'bullets', items: [
          'HeartMate II and 3, HeartWare',
          'Intra-aortic balloon pump',
          'ECMO, both VV and VA',
          'Impella CP and 5.0, TandemHeart',
          'Centrimag, as LVAD or RVAD',
          'Basic post-heart-transplant physiology',
        ] }] },
        { title: 'The right heart after an LVAD', blocks: [{ type: 'bullets', items: [
          'Treat the left and right heart as separate. Both are often sick before surgery, and the LVAD does nothing for the RV, so RV failure is common afterwards',
          'Preload: diuretics',
          'Contractility: epinephrine, dobutamine, milrinone',
          'Afterload: inhaled epoprostenol or nitric oxide',
          'Mechanical: usually a Centrimag on the right',
          'Know the difference between an inotrope and a vasopressor',
        ] }] },
      ],
      sources: [{ title: 'ICU rotation orientation', href: SLICU, detail: 'Blackboard, sign-in required' }],
    },

    teams: {
      title: 'The Two Teams',
      segments: [
        { id: 'vad', label: 'VAD', sections: [
          { title: 'Chronic patients who decompensate fast, plus VA ECMO', blocks: [{ type: 'rows', rows: [
            { label: '06:00', value: 'Sign-out in the team room' },
            { label: '07:30', value: 'Rounds, done by 10 or 11' },
            { label: 'After', value: 'Orders, consults, notes, admissions' },
            { label: 'About 15:00', value: 'Quick afternoon rounds', note: 'Finished early: you may sign out to the call person' },
            { label: '17:30', value: 'Sign-out' },
          ] }] },
          { title: 'Call', blocks: [{ type: 'bullets', items: [
            'Weekday call is 24 hours covering only the VAD patients on 8CA and 8CB overnight. A nurse practitioner covers the CV recovery room',
          ] }] },
        ] },
        { id: 'red', label: 'Red', sections: [
          { title: 'High turnover, postoperative', blocks: [{ type: 'rows', rows: [
            { label: '06:30', value: 'Sign-out in the team office' },
            { label: 'Pre-rounds', value: 'Catch the surgical teams on their rounds, agree the plan, place transfer orders early' },
            { label: '07:30–08:00', value: 'Rounds, done by 10 or 11' },
            { label: 'After', value: 'Orders, consults, notes. Admissions arrive from noon' },
            { label: 'About 15:00', value: 'Quick afternoon rounds', note: 'Afternoons are busy; leaving early is rare' },
            { label: '18:30', value: 'Sign-out' },
          ] }] },
          { title: 'Call', blocks: [{ type: 'bullets', items: [
            'Weekday call is 24 hours with the Red patients day and night, usually carrying your own patients through. Fast-track CABG extubations happen on your watch',
          ] }] },
        ] },
      ],
      sources: [{ title: 'ICU rotation orientation', href: SLICU, detail: 'Blackboard, sign-in required' }],
    },

    day: {
      title: 'Getting Started',
      sections: [
        { title: 'First day', blocks: [{ type: 'bullets', items: [
          'Dr Yang emails an orientation before you start. There is no orientation day; day one is a working day',
          'Know every patient before rounds. Sign-out from the on-call resident or fellow in the team room, then pre-round',
          'Borrow someone’s pre-rounding template',
        ] }] },
        { title: 'Epic', blocks: [{ type: 'bullets', items: [
          'Context: SLEH PHYS CRITICAL CARE AND PULMONARYMED',
          'Patient lists under Shared Lists. The on-call person prints you a list',
        ] }] },
        { title: 'Weekends', blocks: [{ type: 'bullets', items: [
          'One resident or fellow per weekend. Regular start, pre-round, round, finish orders and notes, sign out to the moonlighter at 19:00',
          'No overnight call on weekends',
        ] }] },
        { title: 'Time off', blocks: [{ type: 'bullets', items: [
          { text: 'Requests two months ahead. October by 31 July. Late requests are denied', warn: true },
        ] }] },
      ],
      sources: [{ title: 'ICU rotation orientation', href: SLICU, detail: 'Blackboard, sign-in required' }],
    },
  },
};
